/**
 * Level 94 — Motion.
 *
 * A neighbourhood: detached houses on a grid of plots, streets between them, and
 * a water tower on a plaza in the exact middle of the map. Nearly all of it is
 * `Cell.Open`, so there is no ceiling over the streets and the sky is whatever
 * the fog is — this is the only level in the game with an outside.
 *
 * Two things carry the level. The first is the water tower: it is dead centre
 * and the plaza around it is the only large clearing, so the moment a player
 * sees it they know where they are, which is a sensation this game otherwise
 * never gives them. The second is that the houses are the only cover, the only
 * ceilings, and the only place anything is kept — the supply pass will not put
 * so much as a granola bar on `Cell.Open`, so everything you need is indoors,
 * and going indoors here means walking into somebody's kitchen.
 *
 * Nothing about it is decayed. The grass is one green, the walls are one white,
 * every house is square to the grid, and the whole thing has clearly never been
 * used. It is wrong the way a model village is wrong.
 *
 * The layout is a plot lattice rather than a room partition, and the difference
 * is the whole level:
 *
 *  - A **plot** is a square of lawn with one house set back inside it. It is
 *    registered as its own region, so every plot is a place the placement passes
 *    can put one thing in. Registering the outdoors as a single region — which is
 *    what this used to do — gives an area the size of forty rooms exactly as
 *    much scenery as one room, and the level comes out as bare grass.
 *  - A **lane** is the two-cell strip between two rows of plots, registered as a
 *    `hall`. Rows back onto each other in pairs, so every other lane has front
 *    gardens down both sides of it and the ones between have back fences. That
 *    alternation is what makes a lane read as a street rather than as a gap.
 *  - The **plaza** is a clearing in the middle with nothing built on it.
 *
 * A house is a rectangle of `Cell.Wall`, which is all the grid can say, and the
 * mesh builder draws that as a flat-topped box. What makes it a house rather
 * than a shed the size of a warehouse is scenery fitted to the rectangle: a
 * rounded green mound under it, a pitched roof with a chimney on it, and windows
 * on the faces that show. See `dressBuilding` and `dressHouseTrim`.
 */

import { TUNING } from '../../core/config';
import {
  Cell,
  type Cell2,
  type ExitPoint,
  type Grid,
  type PropFootprint,
  type PropKind,
  type PropSpot,
  type RNG,
  type Vec2,
} from '../../core/types';
import { PROP_DEFS } from './propTables';
import {
  addRegion,
  fillRect,
  interiorRect,
  rectsOverlap,
  strokeRect,
  type GenSite,
  type Rect,
} from './site';

/** Cells of street between two rows of plots. Two is 6.4 m across: a lane. */
const LANE = 2;
/** Plot size in cells, drawn once so the whole neighbourhood is one estate. */
const PLOT: readonly [number, number] = [6, 7];
/**
 * Half-width of the clearing kept around the water tower, in cells. Big enough
 * that the tower is the only thing in it from any angle, and big enough that the
 * placement passes read the plaza as a large open space rather than as more lawn.
 */
const PLAZA_HALF = 7;
/** A plot with nothing built on it. Somewhere to see across. */
const VACANT_CHANCE = 0.12;
/** A house with no way into it. See `buildHouse`. */
const SEALED_CHANCE = 0.22;
/** A plot with a garage on the back of it. */
const GARAGE_CHANCE = 0.3;

/** The four headings, as a cell step. Index order matches `SIDE_YAW`. */
const SIDE_DX = [0, 0, -1, 1] as const;
const SIDE_DY = [-1, 1, 0, 0] as const;
/**
 * Facing, in radians, for a prop that must look out of a plot's front.
 *
 * `yaw = 0` looks down -Z, so a heading (vx, vz) is `atan2(vx, -vz)` — the same
 * convention `props.ts` uses to turn a wall prop out of its wall.
 */
const SIDE_YAW = [0, Math.PI, -Math.PI / 2, Math.PI / 2] as const;

/** One plot: a square of lawn, the house on it, and which way the house faces. */
interface Plot {
  rect: Rect;
  /** Index into `SIDE_DX`/`SIDE_DY`: the lane this plot fronts onto. */
  front: number;
  /** The house footprint including its walls, or null on a vacant lot. */
  house: Rect | null;
  /** The outbuilding at the back, or null. Dressed like a small house. */
  garage: Rect | null;
  /** The front door cell, or null when the house has no way in. */
  door: Cell2 | null;
}

/** One street or back lane between two rows of plots. */
interface Lane {
  rect: Rect;
  /**
   * True when houses front onto it. Every other lane is a street with front
   * gardens down both sides; the ones between are the backs, and they are darker
   * and emptier for it.
   */
  street: boolean;
}

/**
 * What the carving pass worked out, kept for the dressing pass.
 *
 * The two run either side of everything else in `generator.ts` — the grid has to
 * be finished, connected and populated before scenery can be placed against it —
 * and a generator profile has nowhere to hand state forward. Keyed on the grid
 * so the plan belongs to one level and goes away with it.
 */
interface MotionPlan {
  plots: Plot[];
  lanes: Lane[];
  /** Middle of the plaza, in cells. */
  centre: Cell2;
}

const PLANS = new WeakMap<Grid, MotionPlan>();

/** Carve the Motion profile into `site`. */
export function generateMotion(site: GenSite): void {
  const { grid, rng } = site;
  const bounds = interiorRect(grid);

  // Outside first. Everything that is not a house is lawn under an open sky.
  fillRect(grid, bounds, Cell.Open);
  addRegion(site, bounds, 'atrium');

  const midX = grid.w >> 1;
  const midY = grid.h >> 1;
  const plaza: Rect = {
    x: midX - PLAZA_HALF,
    y: midY - PLAZA_HALF,
    w: PLAZA_HALF * 2 + 1,
    h: PLAZA_HALF * 2 + 1,
  };

  const size = rng.int(PLOT[0], PLOT[1]);
  const pitch = size + LANE;
  const xs = lattice(bounds.x, bounds.w, size, pitch);
  const ys = lattice(bounds.y, bounds.h, size, pitch);

  // Lanes are carved by not building on them, but they still have to be named:
  // a street is a different kind of place from a garden, and the placement
  // passes have no other way to know that. Rows face the lane above them when
  // their index is even and the one below when it is odd, so pairs of rows back
  // onto each other: every other horizontal lane is a street with front gardens
  // down both sides, and the ones between are the backs. The cross lanes are
  // alleys either way.
  const lanes: Lane[] = [];
  for (const x of xs) lanes.push({ rect: { x: x - LANE, y: bounds.y, w: LANE, h: bounds.h }, street: false });
  for (let j = 0; j < ys.length; j++) {
    lanes.push({ rect: { x: bounds.x, y: ys[j]! - LANE, w: bounds.w, h: LANE }, street: j % 2 === 0 });
  }
  const lastX = xs[xs.length - 1];
  const lastY = ys[ys.length - 1];
  if (lastX !== undefined) {
    lanes.push({ rect: { x: lastX + size, y: bounds.y, w: bounds.x + bounds.w - lastX - size, h: bounds.h }, street: false });
  }
  if (lastY !== undefined) {
    lanes.push({
      rect: { x: bounds.x, y: lastY + size, w: bounds.w, h: bounds.y + bounds.h - lastY - size },
      street: ys.length % 2 === 0,
    });
  }

  const plots: Plot[] = [];
  for (let j = 0; j < ys.length; j++) {
    for (let i = 0; i < xs.length; i++) {
      const rect: Rect = { x: xs[i]!, y: ys[j]!, w: size, h: size };
      // The tower has to be seen from a distance to be worth anything, so
      // nothing is built on the plaza.
      if (rectsOverlap(rect, plaza, 1)) continue;
      // A plot on the end of a row often turns to face the cross lane instead,
      // the way a corner house does.
      const corner = i === 0 ? 2 : i === xs.length - 1 ? 3 : -1;
      const front = corner >= 0 && rng.chance(0.45) ? corner : j % 2 === 0 ? 0 : 1;
      const plot: Plot = { rect, front, house: null, garage: null, door: null };
      plots.push(plot);
      // Every plot is its own region. This is the single most important line in
      // the file: the passes in `props.ts` roll once per space, so an outdoors
      // registered as one region gets one room's worth of scenery for forty
      // rooms' worth of ground, and Level 94 comes out as mown grass.
      addRegion(site, rect, 'room');
      if (rng.chance(VACANT_CHANCE)) continue;
      buildHouse(site, plot);
      if (plot.house && rng.chance(GARAGE_CHANCE)) garage(site, plot);
    }
  }

  // Registered after the plots and houses so a lane wins the region lookup for
  // its own cells, and the plaza wins over the lanes crossing it.
  for (const lane of lanes) if (lane.rect.w > 0 && lane.rect.h > 0) addRegion(site, lane.rect, 'hall');
  addRegion(site, plaza, 'atrium');

  PLANS.set(grid, { plots, lanes, centre: { cx: midX, cy: midY } });
}

/** Plot origins along one axis, leaving a lane before each one. */
function lattice(start: number, span: number, size: number, pitch: number): number[] {
  const out: number[] = [];
  for (let v = start + LANE; v + size <= start + span; v += pitch) out.push(v);
  return out;
}

/**
 * One house on one plot: a rectangle of wall set back from the lane, most of
 * them hollow with a single door, some of them solid.
 *
 * The solid ones matter more than they sound. A run of houses you can walk into
 * teaches the player that a house is a room; the fourth one, which is the same
 * house and has no way in, is the level telling them something without saying
 * anything.
 */
function buildHouse(site: GenSite, plot: Plot): void {
  const { grid, rng } = site;
  const { rect, front } = plot;
  const w = Math.min(rect.w - 2, rng.int(4, 6));
  const h = Math.min(rect.h - 2, rng.int(4, 5));
  if (w < 4 || h < 4) return;

  // Set back from the lane it fronts onto, and squared up to the grid: the
  // person who laid this out had a plan and no reason to deviate from it. The
  // set-back is what turns the strip between the fence and the door into a
  // front garden, which is the only thing that makes a lawn somebody's.
  const along = front < 2 ? rect.w - w : rect.h - h;
  const across = front < 2 ? rect.h - h : rect.w - w;
  const back = Math.min(across, rng.chance(0.62) ? 2 : 1);
  // Kept a cell off the side boundaries where the plot is big enough to allow
  // it. A house flush against the edge of its own plot has its mound running
  // into next door's, and the thing this level is known for is that every house
  // stands on a hill of its own.
  const slide = along >= 2 ? rng.int(1, along - 1) : rng.int(0, Math.max(0, along));

  let hx: number;
  let hy: number;
  if (front === 0) {
    hx = rect.x + slide;
    hy = rect.y + back;
  } else if (front === 1) {
    hx = rect.x + slide;
    hy = rect.y + rect.h - h - back;
  } else if (front === 2) {
    hx = rect.x + back;
    hy = rect.y + slide;
  } else {
    hx = rect.x + rect.w - w - back;
    hy = rect.y + slide;
  }
  const r: Rect = { x: hx, y: hy, w, h };
  plot.house = r;

  if (rng.chance(SEALED_CHANCE)) {
    fillRect(grid, r, Cell.Wall);
    return;
  }

  strokeRect(grid, r, Cell.Wall);
  const inner: Rect = { x: r.x + 1, y: r.y + 1, w: w - 2, h: h - 2 };
  fillRect(grid, inner, Cell.Floor);

  // A back room in the larger ones, so a house is somewhere to go into rather
  // than somewhere to look into. Carved before the door is cut, so the door can
  // be put where it opens onto a room rather than onto the dividing wall.
  if (w >= 6 && h >= 5 && rng.chance(0.45)) {
    if (rng.chance(0.5)) {
      const x = r.x + 2 + rng.int(0, w - 5);
      fillRect(grid, { x, y: inner.y, w: 1, h: inner.h }, Cell.Wall);
      grid.set(x, inner.y + rng.int(0, inner.h - 1), Cell.Doorway);
    } else {
      const y = r.y + 2 + rng.int(0, h - 5);
      fillRect(grid, { x: inner.x, y, w: inner.w, h: 1 }, Cell.Wall);
      grid.set(inner.x + rng.int(0, inner.w - 1), y, Cell.Doorway);
    }
  }

  plot.door = frontDoor(site, r, plot.front);
  addRegion(site, inner, inner.w * inner.h <= 6 ? 'closet' : 'room');
}

/**
 * One door, in the wall that faces the lane the house was built off, opening
 * onto a cell somebody can stand in.
 */
function frontDoor(site: GenSite, r: Rect, front: number): Cell2 | null {
  const { grid, rng } = site;
  const dx = SIDE_DX[front]!;
  const dy = SIDE_DY[front]!;
  // Walk the face in a shuffled order and take the first cell whose inside is
  // floor — the dividing wall may have landed against any of them.
  const face: Cell2[] = [];
  if (dx === 0) {
    const y = dy < 0 ? r.y : r.y + r.h - 1;
    for (let x = r.x + 1; x < r.x + r.w - 1; x++) face.push({ cx: x, cy: y });
  } else {
    const x = dx < 0 ? r.x : r.x + r.w - 1;
    for (let y = r.y + 1; y < r.y + r.h - 1; y++) face.push({ cx: x, cy: y });
  }
  for (const cell of rng.shuffle(face)) {
    if (grid.at(cell.cx - dx, cell.cy - dy) !== Cell.Floor) continue;
    grid.set(cell.cx, cell.cy, Cell.Doorway);
    return cell;
  }
  return null;
}

/**
 * A garage at the back of the plot: a small solid block, never a way in.
 *
 * It does two jobs. It breaks the sight line down the back lane, which is the
 * only thing out here that does; and it is a wall for the landmark pass to
 * scratch a tally into, on a level that would otherwise have almost none.
 */
function garage(site: GenSite, plot: Plot): void {
  const { grid, rng } = site;
  const house = plot.house;
  if (!house) return;
  const w = rng.int(2, 3);
  const h = 2;
  for (let tries = 0; tries < 8; tries++) {
    const r: Rect = {
      x: plot.rect.x + rng.int(0, Math.max(0, plot.rect.w - w)),
      y: plot.rect.y + rng.int(0, Math.max(0, plot.rect.h - h)),
      w,
      h,
    };
    if (rectsOverlap(r, house, 2)) continue;
    // Behind the house, never in front of it: a garage on the lawn between the
    // gate and the door would be the one thing here nobody would have built.
    const dx = SIDE_DX[plot.front]!;
    const dy = SIDE_DY[plot.front]!;
    const behind = dx === 0 ? (dy < 0 ? r.y >= house.y + house.h : r.y + h <= house.y) : dx < 0 ? r.x >= house.x + house.w : r.x + w <= house.x;
    if (!behind) continue;
    let clear = true;
    for (let y = r.y - 1; y <= r.y + h && clear; y++) {
      for (let x = r.x - 1; x <= r.x + w; x++) {
        if (grid.at(x, y) !== Cell.Open) {
          clear = false;
          break;
        }
      }
    }
    if (!clear) continue;
    fillRect(grid, r, Cell.Wall);
    plot.garage = r;
    return;
  }
}

/* ------------------------------------------------------------------ *
 * Dressing
 * ------------------------------------------------------------------ */

/**
 * Metres of slack around a prop's own box before it claims a cell. The same
 * number `world/gen/propSolid.ts` uses, for the same reason: "claimed" has to
 * mean comfortably impassable rather than a scrape along the side of a car.
 */
const CLEARANCE = TUNING.player.radius + 0.08;

/** Cells between two lamp posts down the same street. About 35 m. */
const LAMP_SPACING = 11;

/* ------------------------------------------------------------------ *
 * Dressing a building
 * ------------------------------------------------------------------ */

/*
 * A house here is a ring of `Cell.Wall` and the mesh builder draws it, so its
 * shape is not something this file can change. What the mesh builder draws is a
 * flat-topped box — wall to `ceilingHeight`, then a collar above it because
 * every lane on this level is `Cell.Open` and an open cell beside a solid one
 * needs something over the ceiling line or the player looks out across the
 * roofs. Left at that, a house is seven metres of blank wallpaper that stops
 * dead, which is a warehouse.
 *
 * Three kinds put the house back, and all three are fitted to a *rectangle of
 * cells* rather than dropped on a spot: `house-hill` is the rounded green mound
 * the building stands on, `house-roof` is the pitch, the eaves and the chimney,
 * and `house-trim` is one bay of windows or the hood over a front door.
 */

/**
 * Smallest and largest building, in cells, that `render/propsubhouse.ts` bakes
 * geometry for. Two is a garage; a house is four or five.
 */
const HOUSE_SIZE_MIN = 2;
const HOUSE_SIZE_MAX = 6;
const HOUSE_SIZES = HOUSE_SIZE_MAX - HOUSE_SIZE_MIN + 1;
/** Ridge along X, ridge along Z, hipped. */
const ROOF_STYLES = 3;
/** Tile, slate, and the green one. */
const ROOF_COLOURS = 3;

/**
 * Index of one building size, in cells, into the baked geometry.
 *
 * `PropSpot.scale` is uniform, so a roof for a five-by-four house is not a
 * scaled roof for a four-by-four one and every size has to be baked separately.
 * This is the same encoding `render/propsubhouse.ts` uses. `world` never
 * imports `render` — that is the rule the whole engine is built on — so the two
 * copies have to be changed together, exactly as a prop's `footprint` and the
 * model that renders it do.
 */
function houseSizeIndex(w: number, d: number): number {
  const a = Math.max(HOUSE_SIZE_MIN, Math.min(HOUSE_SIZE_MAX, w));
  const b = Math.max(HOUSE_SIZE_MIN, Math.min(HOUSE_SIZE_MAX, d));
  return (a - HOUSE_SIZE_MIN) * HOUSE_SIZES + (b - HOUSE_SIZE_MIN);
}

/** Variant for a roof of a given size, shape and colour. */
function roofVariant(w: number, d: number, style: number, colour: number): number {
  return (colour * ROOF_STYLES + style) * HOUSE_SIZES * HOUSE_SIZES + houseSizeIndex(w, d);
}

/** `house-trim` variants, in the order `propsubhouse.ts` bakes them. */
const TRIM_PLAIN = 0;
const TRIM_WIDE = 1;
const TRIM_ORIEL = 2;
const TRIM_PORCH = 3;

/**
 * A collision box whose only job is to keep a roof out of the scenery cull.
 *
 * `render/renderer.ts` switches scenery off past sixty-eight metres and exempts
 * anything whose footprint is six metres tall or more, on the grounds that only
 * a landmark is that big. A roof is not a landmark but it has to follow the same
 * rule: the level's walls are drawn out to a hundred and eighteen metres, so a
 * roof on the ordinary radius would leave every house past the third row flat
 * topped and grow a roof as the player walked toward it.
 *
 * It is five centimetres square, and it is only ever stood on a cell that is
 * already wall, so it blocks nothing that was not already blocked.
 */
const ROOF_MARK: PropFootprint = { halfX: 0.05, halfZ: 0.05, height: 7.2 };

/** What one dressing attempt needs to know. */
interface Dressing {
  grid: Grid;
  rng: RNG;
  out: PropSpot[];
  /** 1 where a solid prop already claims the cell. */
  claimed: Uint8Array;
  /** 1 where a wall bay already carries a window, so no cell gets two. */
  trimmed: Uint8Array;
}

/**
 * Furnish the neighbourhood: a mound and a roof on every building, windows on
 * the walls that show, fences along the front gardens, hedges and trees on the
 * plots, lamp posts down the lanes, a car at the kerb, and the water tower on
 * the plaza.
 *
 * This is a *placement* pass rather than a carving one, so it cannot run from
 * `generateMotion` — the grid has to be finished and populated first. It is
 * written here rather than in `world/gen/props.ts` because everything it knows
 * is about this level: where a plot ends, which side of a house the door is on,
 * which strip of lawn is a street. The general placer draws pockets of scenery
 * against walls, and a suburb is a set of rules about open ground.
 *
 * `world/gen/generator.ts` calls it once, straight after `placeProps` and before
 * the ids are renumbered.
 */
export function dressMotion(site: GenSite, rng: RNG, props: PropSpot[], exits: readonly ExitPoint[]): void {
  const plan = PLANS.get(site.grid);
  if (!plan) return;
  const grid = site.grid;
  const d: Dressing = {
    grid,
    rng,
    out: props,
    claimed: new Uint8Array(grid.cells.length),
    trimmed: new Uint8Array(grid.cells.length),
  };

  // Everything already standing, plus a ring around every way out of the level.
  // An exit the player cannot walk up to is the one failure here nobody could
  // work around.
  for (const spot of props) claim(d, spot.pos, reachOf(spot.kind, spot.scale, spot.yaw));

  // The landmark first: it is the largest thing on the level and the only one
  // whose position is not negotiable. Whatever the general pass happened to drop
  // on the middle of the plaza is cleared out from under it — the tower is what
  // the whole layout is arranged around, and a level that quietly came out
  // without one because a hedge got there first is the one failure here that
  // could not be seen from the ground.
  clearFor(d, 'water-tower', plan.centre.cx, plan.centre.cy);
  // The ways out are claimed after the clearance and before the tower, so the
  // one thing this pass may never do — stand something on an exit — survives it.
  for (const exit of exits) claim(d, exit.pos, { x: exit.radius + 1.2, z: exit.radius + 1.2 });
  place(d, 'water-tower', plan.centre.cx, plan.centre.cy, 0, { variant: 0, scale: 1 });

  // The buildings before the gardens. None of this claims ground — a mound, a
  // roof and a window are all things the player walks past rather than into —
  // so it cannot take a spot away from the planting, and doing it first means
  // the hedges are read against finished houses rather than against blocks.
  for (const plot of plan.plots) {
    if (plot.house) dressBuilding(d, plot.house, plot.front, true);
    if (plot.garage) dressBuilding(d, plot.garage, plot.front, false);
    dressHouseTrim(d, plot);
  }

  for (const plot of plan.plots) dressPlot(d, plot);
  // Only the streets are lit. A back lane with no lamp down it is the one part
  // of this level that gets dark, and it is where the houses have no doors.
  for (const lane of plan.lanes) if (lane.street) lampLane(d, lane.rect);
}

/**
 * World-space reach of a kind's collision box once it has been turned, plus the
 * player's clearance.
 *
 * The turn matters more here than anywhere else in the game, because a fence is
 * 2.4 m by 18 cm: squared off, its box is thirteen times its own area, and two
 * panels laid end to end along a lane would each refuse the other's cell. A
 * fence has to stop you crossing it, not walking beside it.
 */
function reachOf(kind: PropKind, scale: number, yaw: number): Vec2 {
  const fp = PROP_DEFS[kind]!.footprint;
  if (!fp) return { x: CLEARANCE, z: CLEARANCE };
  const c = Math.abs(Math.cos(yaw));
  const s = Math.abs(Math.sin(yaw));
  return {
    x: (fp.halfX * c + fp.halfZ * s) * scale + CLEARANCE,
    z: (fp.halfX * s + fp.halfZ * c) * scale + CLEARANCE,
  };
}

/** Mark every cell whose middle is inside `reach` of a world point. */
function claim(d: Dressing, pos: Vec2, reach: Vec2): void {
  const grid = d.grid;
  const min = grid.worldToCell(pos.x - reach.x, pos.z - reach.z);
  const max = grid.worldToCell(pos.x + reach.x, pos.z + reach.z);
  for (let cy = min.cy; cy <= max.cy; cy++) {
    for (let cx = min.cx; cx <= max.cx; cx++) {
      if (!grid.inBounds(cx, cy)) continue;
      const c = grid.cellToWorld(cx, cy);
      if (Math.abs(c.x - pos.x) > reach.x || Math.abs(c.z - pos.z) > reach.z) continue;
      d.claimed[cy * grid.w + cx] = 1;
    }
  }
}

/**
 * Take back the ground one kind needs at one cell: everything the general pass
 * put there is deleted and its claim released. Only the landmark uses this, and
 * only at the one point on the map that was cleared for it — a plaza with a
 * hedge in the middle of it is a plaza, a plaza with no tower on it is a field.
 */
function clearFor(d: Dressing, kind: PropKind, cx: number, cy: number): void {
  const grid = d.grid;
  const at = grid.cellToWorld(cx, cy);
  const reach = reachOf(kind, 1, 0);
  const inside = (p: Vec2): boolean => Math.abs(p.x - at.x) <= reach.x && Math.abs(p.z - at.z) <= reach.z;
  for (let i = d.out.length - 1; i >= 0; i--) if (inside(d.out[i]!.pos)) d.out.splice(i, 1);

  // Rebuild the claim field over the footprint from what is left standing.
  const min = grid.worldToCell(at.x - reach.x, at.z - reach.z);
  const max = grid.worldToCell(at.x + reach.x, at.z + reach.z);
  for (let y = min.cy; y <= max.cy; y++) {
    for (let x = min.cx; x <= max.cx; x++) {
      if (grid.inBounds(x, y)) d.claimed[y * grid.w + x] = 0;
    }
  }
  for (const spot of d.out) {
    const r = reachOf(spot.kind, spot.scale, spot.yaw);
    if (Math.abs(spot.pos.x - at.x) > reach.x + r.x || Math.abs(spot.pos.z - at.z) > reach.z + r.z) continue;
    claim(d, spot.pos, r);
  }
}

interface PlaceOpts {
  /** Metres off the cell centre, in world space. Half a cell puts a prop on a boundary. */
  dx?: number;
  dz?: number;
  variant?: number;
  scale?: number;
}

/**
 * Stand one prop on one cell, if it fits.
 *
 * A prop may only claim lawn: `Cell.Open` and nothing else. That single rule is
 * what keeps this pass from ever sealing a house, blocking a doorway, standing
 * on a supply cache — all of which are indoors — or fighting with the general
 * placer, whose claims are marked before this runs.
 */
function place(d: Dressing, kind: PropKind, cx: number, cy: number, yaw: number, opts: PlaceOpts = {}): boolean {
  const { grid, rng } = d;
  const def = PROP_DEFS[kind]!;
  const base = grid.cellToWorld(cx, cy);
  const pos: Vec2 = { x: base.x + (opts.dx ?? 0), z: base.z + (opts.dz ?? 0) };
  const scale = opts.scale ?? rng.range(def.scale[0], def.scale[1]);
  const reach = reachOf(kind, scale, yaw);

  const min = grid.worldToCell(pos.x - reach.x, pos.z - reach.z);
  const max = grid.worldToCell(pos.x + reach.x, pos.z + reach.z);
  for (let y = min.cy; y <= max.cy; y++) {
    for (let x = min.cx; x <= max.cx; x++) {
      if (!grid.inBounds(x, y)) return false;
      const c = grid.cellToWorld(x, y);
      if (Math.abs(c.x - pos.x) > reach.x || Math.abs(c.z - pos.z) > reach.z) continue;
      if (grid.cells[y * grid.w + x] !== Cell.Open) return false;
      if (d.claimed[y * grid.w + x]) return false;
    }
  }

  claim(d, pos, reach);
  d.out.push({
    id: 0,
    pos,
    yaw,
    kind,
    height: 0,
    scale,
    variant: opts.variant ?? rng.int(0, 0xffff),
    ...(def.footprint ? { footprint: def.footprint } : {}),
  });
  return true;
}

/**
 * Stand one prop on a building, without asking the ground's permission.
 *
 * `place` refuses anything that is not lawn, which is exactly right for a hedge
 * and useless for a roof: a roof belongs over a rectangle of wall, and a mound
 * belongs around one. Everything placed this way is footprint-free or carries
 * only `ROOF_MARK`, so none of it can block a route, stand on a supply cache or
 * seal a house — the three things that rule was there to prevent.
 */
function placeOver(
  d: Dressing,
  kind: PropKind,
  cx: number,
  cy: number,
  yaw: number,
  variant: number,
  opts: { dx?: number; dz?: number; footprint?: PropFootprint } = {}
): void {
  const base = d.grid.cellToWorld(cx, cy);
  d.out.push({
    id: 0,
    pos: { x: base.x + (opts.dx ?? 0), z: base.z + (opts.dz ?? 0) },
    yaw,
    kind,
    height: 0,
    // Never scaled. Every one of these is cut to the building it is on, and a
    // 1.08 roof is a roof eighty centimetres wider than the house under it.
    scale: 1,
    variant,
    ...(opts.footprint ? { footprint: opts.footprint } : {}),
  });
}

/**
 * A window bay flat against one wall of a building, seen from one lawn cell.
 *
 * `cx, cy` is the lawn cell; `wx, wy` is the step from it into the wall. The
 * prop is stood on the face itself and turned to look out of it, which is the
 * convention every wall-mounted prop in the game is authored to.
 */
function placeAgainst(
  d: Dressing,
  kind: PropKind,
  cx: number,
  cy: number,
  wx: number,
  wy: number,
  variant: number
): boolean {
  const grid = d.grid;
  if (!grid.inBounds(cx, cy)) return false;
  const i = cy * grid.w + cx;
  // Lawn only, and one bay per cell: two windows on one three-metre wall panel
  // would be two windows in the same hole.
  if (grid.cells[i] !== Cell.Open || d.trimmed[i]) return false;
  d.trimmed[i] = 1;

  const base = grid.cellToWorld(cx, cy);
  const half = grid.cellSize * 0.5;
  d.out.push({
    id: 0,
    pos: { x: base.x + wx * half, z: base.z + wy * half },
    // Facing is (sin yaw, -cos yaw), and the bay looks the way the wall does:
    // out of it, which is the opposite of the step that reached it.
    yaw: Math.atan2(-wx, wy),
    kind,
    height: 0,
    scale: 1,
    variant,
  });
  return true;
}

/**
 * Put a mound under one building and a roof on it.
 *
 * Used for the houses and for the garages, which are the same object at a
 * different size — an outbuilding with a flat top and no ground under it is the
 * one thing out here that would read as unfinished rather than as wrong.
 */
function dressBuilding(d: Dressing, r: Rect, front: number, house: boolean): void {
  const { grid, rng } = d;
  const cs = grid.cellSize;

  // The middle of the building, which is a cell boundary whenever the building
  // is an even number of cells across.
  const midX = r.x + ((r.w - 1) >> 1);
  const midY = r.y + ((r.h - 1) >> 1);
  const offX = (r.w / 2 - (((r.w - 1) >> 1) + 0.5)) * cs;
  const offZ = (r.h / 2 - (((r.h - 1) >> 1) + 0.5)) * cs;
  // The mound is authored with its graded front on local -Z and is turned to
  // face the lane. A quarter turn swaps the two axes, so a building fronting
  // an X face asks for the transposed size.
  const acrossX = front < 2;
  placeOver(
    d,
    'house-hill',
    midX,
    midY,
    SIDE_YAW[front]!,
    acrossX ? houseSizeIndex(r.w, r.h) : houseSizeIndex(r.h, r.w),
    { dx: offX, dz: offZ }
  );

  // Authored about the minimum corner cell, so nothing is turned and nothing
  // needs an offset here. The mark only goes on if that cell is still wall:
  // connectivity repair runs before this pass and is allowed to tunnel, and a
  // five-centimetre box in a cell somebody has to walk through is a five
  // centimetre box nobody can see.
  const style = house ? rng.int(0, ROOF_STYLES - 1) : rng.chance(0.5) ? 2 : r.w >= r.h ? 0 : 1;
  const colour = rng.int(0, ROOF_COLOURS - 1);
  placeOver(d, 'house-roof', r.x, r.y, 0, roofVariant(r.w, r.h, style, colour), {
    ...(grid.isSolid(r.x, r.y) ? { footprint: ROOF_MARK } : {}),
  });
}

/**
 * Windows on one house, and the hood over its front door.
 *
 * Only the outward faces get anything, because only the outward faces are what
 * a house shows: the level never lets the player see a wall from inside a plot
 * they cannot walk round. The corners of each face are skipped — a window
 * wrapped round the corner of a building is the one detail out here that would
 * read as a mistake rather than as a model.
 */
function dressHouseTrim(d: Dressing, plot: Plot): void {
  const kind: PropKind = 'house-trim';
  const house = plot.house;
  if (!house) return;
  const { grid, rng } = d;

  // Deliberately small. This level already carries three times the scenery of
  // any other one, and a house is nineteen metres wide: two bays and a door is a
  // front elevation, six is a terrace of shop windows.
  let budget = rng.int(2, 3);
  // The door first: it is the one bay whose place the level decided rather than
  // this pass, and it is the bay worth spending on.
  if (plot.door) {
    const dx = SIDE_DX[plot.front]!;
    const dy = SIDE_DY[plot.front]!;
    if (placeAgainst(d, kind, plot.door.cx + dx, plot.door.cy + dy, -dx, -dy, TRIM_PORCH)) budget--;
  }

  // The face onto the lane is spent first. It is the one a player walks past.
  const near: Array<readonly [number, number, number, number]> = [];
  const far: Array<readonly [number, number, number, number]> = [];
  for (let s = 0; s < 4; s++) {
    const sx = SIDE_DX[s]!;
    const sy = SIDE_DY[s]!;
    const along = sx === 0 ? house.w : house.h;
    for (let k = 1; k < along - 1; k++) {
      const wallX = sx === 0 ? house.x + k : sx < 0 ? house.x : house.x + house.w - 1;
      const wallY = sx === 0 ? (sy < 0 ? house.y : house.y + house.h - 1) : house.y + k;
      (s === plot.front ? near : far).push([wallX + sx, wallY + sy, -sx, -sy]);
    }
  }

  for (const bay of [...rng.shuffle(near), ...rng.shuffle(far)]) {
    if (budget <= 0) break;
    const roll = rng.next();
    const variant = roll < 0.52 ? TRIM_PLAIN : roll < 0.82 ? TRIM_WIDE : TRIM_ORIEL;
    if (placeAgainst(d, kind, bay[0]!, bay[1]!, bay[2]!, bay[3]!, variant)) budget--;
  }
}

/**
 * One plot's worth of garden.
 *
 * The order is the order somebody would have done it in, and it matters: the
 * fence and the gate decide where the path is, the mailbox goes beside the gate,
 * and the planting fills what is left.
 */
function dressPlot(d: Dressing, plot: Plot): void {
  const { rng } = d;
  const { rect, front } = plot;
  const dx = SIDE_DX[front]!;
  const dy = SIDE_DY[front]!;
  const alongX = dx === 0;
  const half = d.grid.cellSize * 0.5;

  // The row of plot cells against the lane, and the gate: the door's own column,
  // so the path from the street to the front door is never fenced across.
  const edgeY = dy < 0 ? rect.y : rect.y + rect.h - 1;
  const edgeX = dx < 0 ? rect.x : rect.x + rect.w - 1;
  const gate = plot.door ? (alongX ? plot.door.cx : plot.door.cy) : -1;
  const from = alongX ? rect.x : rect.y;
  const span = alongX ? rect.w : rect.h;

  // The path from the gate to the front door, spoken for before anything else
  // is placed on the plot. It is the one line of ground on the whole level that
  // has to stay walkable — everything indoors is behind it, including the
  // supplies and, on some seeds, the way down — and reserving it is cheaper and
  // far more reliable than placing scenery and then proving a route survived.
  if (plot.door) {
    let cx = plot.door.cx;
    let cy = plot.door.cy;
    for (let k = 0; k < span + LANE; k++) {
      cx += dx;
      cy += dy;
      if (!d.grid.inBounds(cx, cy)) break;
      d.claimed[cy * d.grid.w + cx] = 1;
      // One cell past the boundary is the kerb: far enough that a car parked
      // across the gate is refused, because its own box reaches this far.
      const past = dy < 0 ? rect.y - cy : dy > 0 ? cy - (rect.y + rect.h - 1) : dx < 0 ? rect.x - cx : cx - (rect.x + rect.w - 1);
      if (past >= 1) break;
    }
  }

  // A fence stands on the boundary rather than in the middle of the cell it is
  // anchored to, and stops one cell short of each corner, so a front garden
  // always has three ways into it and this pass can never wall one off.
  if (rng.chance(0.62)) {
    const yaw = alongX ? 0 : Math.PI / 2;
    // One height of fence per plot: a garden whose boundary changes height
    // halfway along is two gardens.
    const style = rng.chance(0.5) ? 0 : 2;
    for (let k = 1; k < span - 1; k++) {
      const v = from + k;
      if (v === gate) continue;
      const cx = alongX ? v : edgeX;
      const cy = alongX ? edgeY : v;
      place(d, 'picket-fence', cx, cy, yaw, {
        dx: alongX ? 0 : dx * half,
        dz: alongX ? dy * half : 0,
        // The gate itself is hung on the panel beside the path.
        variant: gate >= 0 && Math.abs(v - gate) === 1 ? 1 : style,
      });
    }
  }

  if (gate >= 0 && rng.chance(0.55)) {
    const v = gate + (rng.chance(0.5) ? 1 : -1);
    const cx = alongX ? v : edgeX;
    const cy = alongX ? edgeY : v;
    place(d, 'mailbox', cx, cy, SIDE_YAW[front]!, { dx: alongX ? 0 : dx * half * 0.6, dz: alongX ? dy * half * 0.6 : 0 });
  }

  // Planting. Candidates are drawn from the whole plot and the ones that do not
  // fit are simply dropped — a plot with a house filling it gets a hedge in the
  // one corner that is left, which is what a small garden looks like.
  const cells = plotCells(d, plot);
  d.rng.shuffle(cells);
  let trees = (rng.chance(0.5) ? 1 : 0) + (rng.chance(0.14) ? 1 : 0);
  let hedges = rng.chance(0.72) ? rng.int(1, 2) : 0;
  let garden = rng.chance(0.26) ? 1 : 0;
  for (const cell of cells) {
    if (trees <= 0 && hedges <= 0 && garden <= 0) break;
    const cx = cell % d.grid.w;
    const cy = (cell / d.grid.w) | 0;
    if (garden > 0) {
      const kind: PropKind = rng.chance(0.5) ? 'swing-set' : 'laundry-line';
      if (place(d, kind, cx, cy, rng.chance(0.5) ? 0 : Math.PI / 2)) {
        garden--;
        continue;
      }
    }
    if (trees > 0 && place(d, 'garden-tree', cx, cy, 0)) {
      trees--;
      continue;
    }
    // Half the wander it used to have. Every house now stands in a bank of lawn
    // and a hedge shoved 80 cm off its cell can end up a third of the way into
    // one; at 50 the worst case is a couple of centimetres, which reads as a
    // shrub growing on a slope rather than as one sinking through it.
    if (hedges > 0 && place(d, 'shrub', cx, cy, 0, { dx: rng.range(-0.5, 0.5), dz: rng.range(-0.5, 0.5) })) {
      hedges--;
    }
  }

  // A car at the kerb, in the lane and pointing down it. Never within two cells
  // of the gate: a car is four metres long and would be parked across the path.
  if (rng.chance(0.22)) {
    const lanes: number[] = [];
    for (let k = 0; k < span; k++) {
      const v = from + k;
      if (gate >= 0 && Math.abs(v - gate) <= 2) continue;
      lanes.push(v);
    }
    for (const v of rng.shuffle(lanes)) {
      const cx = alongX ? v : edgeX + dx;
      const cy = alongX ? edgeY + dy : v;
      const ok = place(d, 'parked-car', cx, cy, alongX ? Math.PI / 2 : 0, {
        dx: alongX ? 0 : dx * 0.9,
        dz: alongX ? dy * 0.9 : 0,
      });
      if (ok) break;
    }
  }
}

/** Every lawn cell of a plot that nothing has claimed yet. */
function plotCells(d: Dressing, plot: Plot): number[] {
  const grid = d.grid;
  const out: number[] = [];
  for (let y = plot.rect.y; y < plot.rect.y + plot.rect.h; y++) {
    for (let x = plot.rect.x; x < plot.rect.x + plot.rect.w; x++) {
      if (!grid.inBounds(x, y)) continue;
      const i = y * grid.w + x;
      if (grid.cells[i] !== Cell.Open || d.claimed[i]) continue;
      out.push(i);
    }
  }
  return out;
}

/**
 * Lamp posts down one lane, evenly spaced and always on the same side of it.
 *
 * Evenly is the point. Everything else on this level is jittered a few
 * centimetres off true; a line of lamp posts at exactly twenty-two metres is the
 * one thing out here that was measured, and it is what makes a strip of grass
 * between two rows of houses read as a street.
 */
function lampLane(d: Dressing, lane: Rect): void {
  const grid = d.grid;
  const alongX = lane.w >= lane.h;
  const length = alongX ? lane.w : lane.h;
  const kerb = alongX ? lane.y : lane.x;
  for (let k = LAMP_SPACING >> 1; k < length; k += LAMP_SPACING) {
    const cx = alongX ? lane.x + k : kerb;
    const cy = alongX ? kerb : lane.y + k;
    if (!grid.inBounds(cx, cy)) continue;
    place(d, 'lamp-post', cx, cy, 0);
  }
}
